import React, { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames/bind";
import Modal from "react-modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronUp,
    faChevronDown,
    faTimes
} from "@fortawesome/free-solid-svg-icons";
import { widenResize, isWidenImage } from "helpers";
import styles from "./ImageNav.module.scss";

const cx = classNames.bind(styles);

/**
 * Component for displaying Image Navigation.
 */
class ImageNav extends Component {
    state = {
        displayVideoModal: false,
        videoModalUrl: "",
        usePaddles: false,
        exceedsVisibleImageLimit: false,
        visibleImagesOffset: 0,
        visibleImages: []
    };

    componentDidMount() {
        const { images, visibleImageLimit, displayAll } = this.props;

        Modal.setAppElement("body");

        this.setState(prevState => {
            return {
                ...prevState,
                exceedsVisibleImageLimit:
                    images.length >= visibleImageLimit && !displayAll,
                visibleImages: this.getInitialVisibleImages()
            };
        });
    }

    componentDidUpdate(prevProps) {
        const { images, visibleImageLimit, displayAll } = this.props;

        Modal.setAppElement("body");

        if ( this.props.activeImage !== prevProps.activeImage ) {
            this.setState(prevState => {
                return {
                    ...prevState,
                    exceedsVisibleImageLimit:
                        images.length >= visibleImageLimit && !displayAll,
                    visibleImages: this.getInitialVisibleImages()
                };
            });
        }
    }

    openVideoModal() {
        this.setState({ displayVideoModal: true });
    }

    closeVideoModal() {
        this.setState({ displayVideoModal: false });
    }

    /**
     * Handles the pager click event.
     *
     * @param {array} image The current image.
     * @param {boolean} isImage If the pager is for an image.
     * @param {boolean} isLastVisibleImage If the pager is the last visible image.
     */
    handlePagerClick(image, isImage = true, isLastVisibleImage = false) {
        if (!isImage && (!isLastVisibleImage || this.state.usePaddles)) {
            this.setState({ videoModalUrl: image.image });
            this.openVideoModal();
        }

        this.props.onChange(image.hash);
    }

    /**
     * Returns if an image is the active image.
     *
     * @param {string} hash The current item's hash.
     * @return {boolean}
     */
    isActiveImage(hash) {
        let isActiveImage = false;

        if (hash === this.props.activeImage?.hash) {
            isActiveImage = true;
        }

        return isActiveImage;
    }

    /**
     * Returns if an image is the last visible image.
     *
     * @param {number} index The index of the current image in our images array.
     * @return {boolean}
     */
    isLastVisibleImage(index) {
        let isLastImage = false;

        if (index + 1 === this.props.visibleImageLimit) {
            isLastImage = true;
        }

        return isLastImage;
    }

    /**
     * Returns the remaining image count.
     *
     * @param {number} totalImageCount
     * @return {number}
     */
    getRemainingImageCount(totalImageCount) {
        return totalImageCount - this.props.visibleImageLimit + 1;
    }

    /**
     * Returns the array of containing the initial visible images.
     *
     * @return {Array}
     */
    getInitialVisibleImages() {
        const { images: allImages, visibleImageLimit, displayAll } = this.props;
        let visibleImages = allImages.slice(0, visibleImageLimit);

        if (displayAll) {
            visibleImages = allImages;
        }

        return visibleImages;
    }

    /**
     * Returns the max offset for the visible images array.
     *
     * @return {number}
     */
    getVisibleImagesOffsetMax() {
        const { images: allImages, visibleImageLimit } = this.props;
        const visibleImagesOffsetMax = allImages.length - visibleImageLimit;

        return visibleImagesOffsetMax;
    }

    /**
     * Displays the paddle navigation for the images.
     */
    usePaddles() {
        this.setState(prevState => {
            return {
                ...prevState,
                usePaddles: true
            };
        });
    }

    /**
     * Handles the `previous` paddle click event.
     */
    handlePrevPaddle() {
        const { images: allImages, visibleImageLimit } = this.props;

        this.setState(prevState => {
            const visibleImagesOffset =
                prevState.visibleImagesOffset > 0
                    ? prevState.visibleImagesOffset - 1
                    : 0;

            const visibleImagesIndexBegin = visibleImagesOffset;
            const visibleImagesIndexEnd =
                visibleImageLimit + visibleImagesOffset;

            const visibleImages = allImages.slice(
                visibleImagesIndexBegin,
                visibleImagesIndexEnd
            );

            return {
                ...prevState,
                visibleImages,
                visibleImagesOffset
            };
        });
    }

    /**
     * Handles the `Next` paddle click event.
     */
    handleNextPaddle() {
        const { images: allImages, visibleImageLimit } = this.props;
        const visibleImagesOffsetMax = this.getVisibleImagesOffsetMax();

        this.setState(prevState => {
            const visibleImagesOffset =
                prevState.visibleImagesOffset < visibleImagesOffsetMax
                    ? prevState.visibleImagesOffset + 1
                    : visibleImagesOffsetMax;

            const visibleImagesIndexBegin = visibleImagesOffset;
            const visibleImagesIndexEnd =
                visibleImageLimit + visibleImagesOffset;

            const visibleImages = allImages.slice(
                visibleImagesIndexBegin,
                visibleImagesIndexEnd
            );

            return {
                ...prevState,
                visibleImages,
                visibleImagesOffset
            };
        });
    }

    render() {
        const { images, displayAll } = this.props;
        const { visibleImages } = this.state;

        return (
            <>
                <nav
                    className={cx("ImageNav", {
                        "ImageNav--DisplayAll": displayAll
                    })}
                >
                    {this.state.usePaddles && (
                        <button
                            className={cx("ImageNav__Paddle")}
                            onClick={() => this.handlePrevPaddle()}
                            disabled={this.state.visibleImagesOffset === 0}
                        >
                            <FontAwesomeIcon icon={faChevronUp} />
                        </button>
                    )}
                    <ul aria-hidden="true" className={cx("ImageNav__Items")}>
                        {visibleImages.map((image, index) => {
                            const isImage = isWidenImage(image.image_thumb);
                            const isLastVisibleImage = this.isLastVisibleImage(
                                index
                            );

                            return (
                                <li
                                    key={index}
                                    className={cx(
                                        "ImageNav__Item",
                                        {
                                            "ImageNav__Item--Active": this.isActiveImage(
                                                image.hash
                                            )
                                        },
                                        {
                                            "ImageNav__Item--Last": isLastVisibleImage
                                        }
                                    )}
                                    onClick={() =>
                                        this.handlePagerClick(
                                            image,
                                            isImage,
                                            isLastVisibleImage
                                        )
                                    }
                                >
                                    {isImage ? (
                                        <img
                                            className={cx(["ImageNav__Image"])}
                                            alt={image.alt_text}
                                            src={widenResize(
                                                image.image_thumb,
                                                64,
                                                64
                                            )}
                                        />
                                    ) : (
                                        <div
                                            className={cx([
                                                "ImageNav__ItemOverlay"
                                            ])}
                                        >
                                            <iframe
                                                tabIndex={-1} // Prevent user from being able to focus on the video.
                                                className={cx(
                                                    "ImageNav__Item--Video"
                                                )}
                                                src={image.image}
                                                frameBorder={0}
                                                scrolling="no"
                                            />
                                        </div>
                                    )}
                                    {!displayAll &&
                                        !this.state.usePaddles &&
                                        this.isLastVisibleImage(index) && (
                                            <div
                                                className={cx([
                                                    "ImageNav__ItemOverlay"
                                                ])}
                                                onClick={() =>
                                                    this.usePaddles()
                                                }
                                            >
                                                +
                                                {this.getRemainingImageCount(
                                                    images.length
                                                )}
                                            </div>
                                        )}
                                </li>
                            );
                        })}
                    </ul>
                    {this.state.usePaddles && (
                        <button
                            className={cx("ImageNav__Paddle")}
                            onClick={() => this.handleNextPaddle()}
                            disabled={
                                this.state.visibleImagesOffset ===
                                this.getVisibleImagesOffsetMax()
                            }
                        >
                            <FontAwesomeIcon icon={faChevronDown} />
                        </button>
                    )}
                </nav>
                <Modal
                    isOpen={this.state.displayVideoModal}
                    onRequestClose={() => this.closeVideoModal()}
                    style={{
                        content: {
                            padding: 0,
                            border: 0,
                            overflow: "visible" // Display close button outside of video area.
                        }
                    }}
                >
                    <div className={cx("VideoModal__Header")}>
                        <button
                            className={cx("VideoModal__CloseButton")}
                            onClick={() => this.closeVideoModal()}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                    <iframe
                        className={cx("VideoModal__Iframe")}
                        src={this.state.videoModalUrl}
                        frameBorder={0}
                        webkitallowfullscreen="true"
                        mozallowfullscreen="true"
                        allowFullScreen={true}
                        allowtransparency="true"
                        scrolling="no"
                    />
                </Modal>
            </>
        );
    }
}

ImageNav.propTypes = {
    /** Array of images. */
    images: PropTypes.arrayOf(
        PropTypes.shape({
            hash: PropTypes.string.isRequired,
            name: PropTypes.string.name.isRequired,
            image_thumb: PropTypes.string.isRequired
        })
    ).isRequired,
    /** The number of images that can be shown before paddle navigation is required. This includes the last overlay tile. */
    visibleImageLimit: PropTypes.number,
    /** The current active image object. */
    activeImage: PropTypes.shape({
        hash: PropTypes.string.isRequired
    }),
    /** Fires when a user clicks an image. */
    displayAll: PropTypes.bool,
    /** Fires when a user clicks an image. */
    onChange: PropTypes.func.isRequired
};

ImageNav.defaultProps = {
    activeImage: null,
    visibleImageLimit: 7,
    displayAll: false
};

export default ImageNav;
